import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/kestrelApi'
import { omit } from 'lodash'
import { Vector2 } from 'three'
import { KVAnnotation, RolodexEntryPatchJson } from 'types'

import { KVMessageDef } from './index'

export function getNewAnnotationMessage() {
  return {
    type: 'newAnnotation',
    requiredData: ['annotationId'],

    callback: async (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const annotation = KVAnnotation.fromJSON(json)
      annotation.userName = kv.user?.name ?? ''
      annotation.userEmail = kv.user?.email ?? ''

      if (kv.annotations) {
        kv.annotations.push(annotation)
      }
      kv.setAnnotations(kv.annotations)

      // Position popup in top right of screen
      const kvElementRect = kv.videoParent.current?.getBoundingClientRect()
      if (kvElementRect) {
        // Position in top right with some margin from edges
        const topRightX = kvElementRect.width - 375
        const topRightY = 20
        kv.setPopupMenuStartPosition(new Vector2(topRightX, topRightY))
      }
      kv.setSelectedMarkup(annotation)

      // Track annotation in rolodex
      const patchData: RolodexEntryPatchJson = {
        id: kv.kestrelModel?.id ?? '',
        data: {
          annotations: kv.annotations?.map((a) => KVAnnotation.toJSON(a)) ?? [],
        },
      }
      kv.rolodexPatch([patchData])

      // Filter photos to that location
      if (!kv.kestrelModel?.id) {
        console.warn('No kestrel model available for nearby photos API')
        return
      }

      try {
        kv.setFilteringPhotos(true)
        const positionRegex = /X=(-?\d+\.\d+) Y=(-?\d+\.\d+) Z=(-?\d+\.\d+)/
        const match = annotation.position.match(positionRegex)
        if (match) {
          const worldPosition = {
            x: parseFloat(match[1]),
            y: parseFloat(match[2]),
            z: parseFloat(match[3]),
          }
          const photoResponses = await fetchNearbyPhotos(
            kv.kestrelModel.id,
            worldPosition,
            kv.authorizedAxios,
            kv.kestrelApiUrl
          )

          const projectionMap = new Map(
            photoResponses.map((photoResponse) => [
              photoResponse.image_name,
              {
                filename: photoResponse.image_name,
                x: photoResponse.projection.x,
                y: photoResponse.projection.y,
                originalImageWidth: photoResponse.width,
                originalImageHeight: photoResponse.height,
              },
            ])
          )

          const updatedPhotos = kv.photos.map((photo) => {
            const projection = projectionMap.get(photo.filename)
            return projection
              ? { ...photo, projection }
              : omit(photo, 'projection')
          })

          kv.setPhotos(updatedPhotos)

          const filteredPhotos = updatedPhotos.filter(
            (photo) => 'projection' in photo && photo.projection
          )
          kv.setFilteredPhotos(
            filteredPhotos.length > 0 ? filteredPhotos : null
          )
        } else {
          console.error('Invalid position format:', annotation.position)
        }
      } catch (error) {
        console.error(
          'Failed to fetch nearby photos for orbit position:',
          error
        )
        kv.setFilteredPhotos(null)
        const clearedPhotos = kv.photos.map((photo) =>
          omit(photo, 'projection')
        )
        kv.setPhotos(clearedPhotos)
      }
      kv.setFilteringPhotos(false)
    },
  } as KVMessageDef
}
